import { useEffect, useState } from 'react'
import { annulerCommande } from '../api/dispatch'
import { montantRemboursable, MOTIF_MAX, validerMotif } from '../api/annulation'
import type { Commande } from '../api/types'
import { fcfa } from './Format'
import { Modale } from './Modale'

/**
 * Annulation d'une commande par le dispatch : motif OBLIGATOIRE, puis confirmation
 * qui montre ce qui sera remboursable au client.
 *
 * Le serveur (`set-order-status`) refait toutes les vérifications — statut permis,
 * motif non vide, montant. Ici on évite seulement un aller-retour pour une faute
 * évidente, et on dit les refus en phrases.
 */
export function AnnulerCommande({
  commande,
  surFermer,
  surAnnulee,
}: {
  commande: Commande | null
  surFermer: () => void
  /** Appelée une fois la commande annulée côté serveur. */
  surAnnulee: () => Promise<void>
}) {
  const [motif, setMotif] = useState('')
  const [etape, setEtape] = useState<'saisie' | 'confirmation'>('saisie')
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const id = commande?.id ?? null

  useEffect(() => {
    setMotif('')
    setEtape('saisie')
    setErreur(null)
    setOccupe(false)
  }, [id])

  if (!commande) return <Modale titre="" ouverte={false} surFermer={surFermer}>{null}</Modale>

  const rembourse = montantRemboursable(commande.statut, commande.total)
  const valide = validerMotif(motif)
  const motifNet = valide.ok ? valide.motif : motif

  function continuer() {
    const r = validerMotif(motif)
    if (!r.ok) {
      setErreur(r.message)
      return
    }
    setErreur(null)
    setEtape('confirmation')
  }

  async function confirmer() {
    const r = validerMotif(motif)
    if (!r.ok) {
      setEtape('saisie')
      setErreur(r.message)
      return
    }
    setOccupe(true)
    setErreur(null)
    try {
      await annulerCommande(commande!.id, r.motif)
      await surAnnulee()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Annulation impossible.')
    } finally {
      setOccupe(false)
    }
  }

  return (
    <Modale titre={`Annuler la commande ${commande.ref ?? ''}`} ouverte surFermer={surFermer}>
      {etape === 'saisie' ? (
        <>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--n700)' }}>
            {commande.clientNom} — {commande.marchandNom} — {fcfa(commande.total)}
          </p>
          <label htmlFor="motif-annulation">Motif de l’annulation (obligatoire)</label>
          <textarea
            id="motif-annulation"
            value={motif}
            maxLength={MOTIF_MAX}
            placeholder="Par exemple : client injoignable, marchand fermé, rupture de stock…"
            onChange={(e) => setMotif(e.target.value)}
            autoFocus
          />
          <p style={{ fontSize: 11.5, color: 'var(--n400)', margin: '4px 0 0' }}>
            Ce motif est conservé avec la commande et visible du marchand et du client.
          </p>
          {erreur && <p style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>{erreur}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn btn-outline" onClick={surFermer}>Retour</button>
            <button className="btn btn-accent" onClick={continuer}>Continuer</button>
          </div>
        </>
      ) : (
        <>
          <div className="avis avis-warn" style={{ marginTop: 0 }}>
            Vous allez annuler la commande <b>{commande.ref}</b> de <b>{commande.clientNom}</b>.{' '}
            {rembourse > 0 ? (
              <>
                Montant remboursable au client : <b>{fcfa(rembourse)}</b> (le total, annulation par le dispatch).
              </>
            ) : (
              <>Le client n’a encore rien payé : <b>aucun remboursement</b> n’est dû.</>
            )}
          </div>
          <p style={{ fontSize: 13, margin: '12px 0 0' }}>
            <b>Motif :</b> {motifNet}
          </p>
          {rembourse > 0 && (
            <p style={{ fontSize: 11.5, color: 'var(--n400)', margin: '8px 0 0' }}>
              Cette console ne vire aucun argent : le remboursement apparaît dans l’onglet Paiements,
              à régler chez l’opérateur puis à marquer « remboursé ».
            </p>
          )}
          {commande.livreurId && (
            <p style={{ fontSize: 11.5, color: 'var(--n400)', margin: '8px 0 0' }}>
              Le livreur {commande.livreurNom ?? ''} est déjà affecté : pensez à le prévenir.
            </p>
          )}
          {erreur && <p style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>{erreur}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn btn-outline" disabled={occupe} onClick={() => setEtape('saisie')}>
              Modifier le motif
            </button>
            <button className="btn btn-danger-sm" disabled={occupe} onClick={() => void confirmer()}>
              {occupe ? 'Annulation…' : 'Confirmer l’annulation'}
            </button>
          </div>
        </>
      )}
    </Modale>
  )
}
